import type { Agent, AgentOutputType } from '../agent';
import type { Handoff } from '../handoff';
import type { RunState } from '../run-state';
import type { Tool } from '../tool';
import { serializeHandoff, serializeTool } from '../utils/serialize';
import { validateClientToolSearchSupport } from './tool-search';
import { ensureAgentSpan } from './tracing';
import type { AgentArtifacts } from './types';

/**
 * Collects tools and handoffs for the current agent so model calls and tracing share the same
 * snapshot of enabled capabilities.
 */
export async function prepareAgentArtifacts<
  TContext,
  TAgent extends Agent<TContext, AgentOutputType>,
>(
  state: RunState<TContext, TAgent>,
  executionAgent: Agent<TContext, AgentOutputType> = state._currentAgent,
): Promise<AgentArtifacts<TContext>> {
  const capabilities = await collectAgentCapabilities(state, executionAgent);
  validateClientToolSearchSupport(capabilities.tools);
  state.setCurrentAgentSpan(
    ensureAgentSpan({
      agent: executionAgent,
      handoffs: capabilities.handoffs,
      tools: capabilities.tools,
      currentSpan: state._currentAgentSpan,
    }),
  );

  return {
    ...capabilities,
    serializedHandoffs: capabilities.handoffs.map((handoff) =>
      serializeHandoff(handoff),
    ),
    serializedTools: capabilities.tools.map((tool) => serializeTool(tool)),
    toolsExplicitlyProvided: executionAgent.hasExplicitToolConfig(),
  };
}

async function collectAgentCapabilities<TContext>(
  state: RunState<TContext, Agent<TContext, AgentOutputType>>,
  executionAgent: Agent<TContext, AgentOutputType>,
): Promise<{
  handoffs: Handoff<any, any>[];
  tools: Tool<TContext>[];
}> {
  const handoffs = await executionAgent.getEnabledHandoffs(state._context);
  const configuredTools = (await executionAgent.getAllTools(
    state._context,
  )) as Tool<TContext>[];
  // const runtimeLoadedTools = state.getToolSearchRuntimeTools(
  //   state._currentAgent,
  // ) as Tool<TContext>[];
  return { handoffs, tools: [...configuredTools /*...runtimeLoadedTools*/] };
}
